import { useState, memo } from "react";

/**
 * RevenueChart — SVG line chart showing Invoiced / Received / Expenses trend.
 *
 * Props:
 *   data — array of { label, invoiced, received, expenses, net }
 */

const LINES = [
  { key: "invoiced", color: "#0C2D48", label: "Invoiced" },
  { key: "received", color: "#1e6645", label: "Received" },
  { key: "expenses", color: "#A12710", label: "Expenses" },
];

function fmtMoney(n) {
  if (n >= 1000) return `£${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return `£${n.toFixed(0)}`;
}

function fmtFull(n) {
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function RevenueChart({ data }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: "center", color: "#bbb", padding: "2rem", fontSize: "0.88rem" }}>
        No data for this period.
      </div>
    );
  }

  // ── SVG layout constants ──────────────────────────────────────────────────
  const W   = 700;
  const H   = 240;
  const PAD = { top: 20, right: 24, bottom: 52, left: 68 };
  const PW  = W - PAD.left - PAD.right;   // plot width
  const PH  = H - PAD.top  - PAD.bottom;  // plot height

  // ── Y scale ───────────────────────────────────────────────────────────────
  const allVals = data.flatMap(d => [d.invoiced, d.received, d.expenses]);
  const maxVal  = Math.max(...allVals, 1);
  // Nice round max: next multiple of 500 (or 100 for small values)
  const step = maxVal < 2000 ? 100 : maxVal < 10000 ? 500 : 1000;
  const niceMax = Math.ceil(maxVal / step) * step;
  const yTicks  = 5;

  // ── Coordinate helpers ────────────────────────────────────────────────────
  const n = data.length;
  const xOf = i => PAD.left + (n === 1 ? PW / 2 : (i / (n - 1)) * PW);
  const yOf = v => PAD.top + PH - Math.max(0, (v / niceMax) * PH);

  function polylinePoints(key) {
    return data.map((d, i) => `${xOf(i).toFixed(1)},${yOf(d[key]).toFixed(1)}`).join(" ");
  }

  // ── Tooltip geometry ──────────────────────────────────────────────────────
  const tip = hoveredIdx != null ? data[hoveredIdx] : null;
  const tipX = tip ? xOf(hoveredIdx) : 0;
  const tipW = 158;
  const tipH = 76;
  const tipXAdj = tipX + tipW + 10 > W - PAD.right ? tipX - tipW - 10 : tipX + 10;
  const tipY = PAD.top + 8;

  // ── Hover area width per bucket ───────────────────────────────────────────
  const areaW = n === 1 ? PW : PW / (n - 1);

  return (
    <div>
      {/* Legend */}
      <div style={{ display: "flex", gap: "1.5rem", marginBottom: "0.85rem", flexWrap: "wrap" }}>
        {LINES.map(l => (
          <div key={l.key} style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontFamily: "Raleway, sans-serif" }}>
            <div style={{ width: 26, height: 3, background: l.color, borderRadius: 2, flexShrink: 0 }} />
            <span style={{ fontSize: "0.78rem", fontWeight: 600, color: l.color }}>{l.label}</span>
          </div>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", display: "block", overflow: "visible" }}
        aria-label="Revenue trend chart"
      >
        {/* Y axis grid lines + labels */}
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const val = (niceMax / yTicks) * i;
          const y = yOf(val);
          return (
            <g key={i}>
              <line
                x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                stroke={i === 0 ? "#ddd" : "#f2f2f2"} strokeWidth={1}
              />
              <text
                x={PAD.left - 8} y={y + 4}
                textAnchor="end" fontSize={10}
                fill="#aaa" fontFamily="Raleway, sans-serif"
              >
                {fmtMoney(val)}
              </text>
            </g>
          );
        })}

        {/* X axis base line */}
        <line
          x1={PAD.left} y1={PAD.top + PH}
          x2={W - PAD.right} y2={PAD.top + PH}
          stroke="#ddd" strokeWidth={1}
        />

        {/* X axis labels */}
        {data.map((d, i) => (
          <text
            key={i}
            x={xOf(i)} y={PAD.top + PH + 18}
            textAnchor="middle" fontSize={9}
            fill="#999" fontFamily="Raleway, sans-serif"
          >
            {d.label}
          </text>
        ))}

        {/* Data lines */}
        {LINES.map(l => (
          <polyline
            key={l.key}
            points={polylinePoints(l.key)}
            fill="none"
            stroke={l.color}
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* Data dots */}
        {LINES.map(l =>
          data.map((d, i) => (
            <circle
              key={`${l.key}-${i}`}
              cx={xOf(i)} cy={yOf(d[l.key])} r={hoveredIdx === i ? 5 : 3.5}
              fill="#fff" stroke={l.color} strokeWidth={2}
            />
          ))
        )}

        {/* Hover vertical rule */}
        {hoveredIdx != null && (
          <line
            x1={xOf(hoveredIdx)} y1={PAD.top}
            x2={xOf(hoveredIdx)} y2={PAD.top + PH}
            stroke="#ccc" strokeWidth={1} strokeDasharray="4,3"
          />
        )}

        {/* Transparent hover capture areas */}
        {data.map((_, i) => (
          <rect
            key={i}
            x={xOf(i) - areaW / 2} y={PAD.top}
            width={areaW} height={PH}
            fill="transparent"
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{ cursor: "crosshair" }}
          />
        ))}

        {/* Tooltip (SVG native, no HTML overlay) */}
        {tip && (
          <g>
            <rect
              x={tipXAdj} y={tipY}
              width={tipW} height={tipH}
              rx={6} ry={6}
              fill="#0C2D48"
              style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.25))" }}
            />
            <text
              x={tipXAdj + 10} y={tipY + 16}
              fontSize={10} fontWeight={700}
              fill="#fff" fontFamily="Raleway, sans-serif"
            >
              {tip.label}
            </text>
            {LINES.map((l, li) => (
              <text
                key={l.key}
                x={tipXAdj + 10} y={tipY + 32 + li * 15}
                fontSize={9} fill={l.color === "#0C2D48" ? "#82B3CC" : l.color}
                fontFamily="Raleway, sans-serif"
              >
                {l.label}: {fmtFull(tip[l.key])}
              </text>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
}

export default memo(RevenueChart);
